/**
 * Configuración (sección 7): ajustes globales del sistema. Cuatro sub-secciones:
 * parámetros de evaluación, dialectos LATAM, notificaciones e integraciones.
 * Cada guardado pide confirmación explícita (afecta producción) y queda en el
 * audit trail. Los parámetros del motor determinista/versionado (§6.4, versiones)
 * se muestran SOLO LECTURA — no son configurables (reproducibilidad clínica).
 */
import { useEffect, useState } from "react";
import { adminApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { ActualizarConfigInput, AdministrationOrderDefault } from "../../api/types.js";
import { Async, ConfirmDialog, fmtFecha } from "./ui.js";
import s from "./admin.module.css";

const ORDER_LABEL: Record<AdministrationOrderDefault, string> = {
  DENOMINACION_PRIMERO: "Denominación primero",
  REPETICION_PRIMERO: "Repetición primero",
};

interface Pendiente {
  titulo: string;
  patch: ActualizarConfigInput;
}

export function AdminConfig() {
  const q = useQuery(() => adminApi.config());
  const [order, setOrder] = useState<AdministrationOrderDefault>("DENOMINACION_PRIMERO");
  const [dialectos, setDialectos] = useState<string[]>([]);
  const [nuevoDialecto, setNuevoDialecto] = useState("");
  const [notif, setNotif] = useState<Record<string, boolean>>({});
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sincroniza el estado local con la config cargada (y tras cada guardado).
  useEffect(() => {
    if (!q.data) return;
    setOrder(q.data.editable.administrationOrderDefault);
    setDialectos(q.data.editable.dialectosHabilitados);
    setNotif(q.data.editable.notificaciones);
  }, [q.data]);

  async function ejecutar() {
    if (!pendiente) return;
    setOcupado(true);
    setError(null);
    try {
      await adminApi.actualizarConfig(pendiente.patch);
      setPendiente(null);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  function agregarDialecto() {
    const d = nuevoDialecto.trim();
    if (d && !dialectos.includes(d)) setDialectos([...dialectos, d]);
    setNuevoDialecto("");
  }

  return (
    <>
      <div className={s.sectionTitle}>Configuración</div>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}

      <Async q={q}>
        {(cfg) => (
          <>
            {cfg.editable.ultimoCambio ? (
              <div className={`${s.banner} ${s.bannerInfo}`}>
                Último cambio por <b>{cfg.editable.ultimoCambio.porNombre ?? "—"}</b> el{" "}
                {fmtFecha(cfg.editable.ultimoCambio.en)}.
              </div>
            ) : null}

            {/* 1. Parámetros de evaluación */}
            <div className={`${s.panel} ${s.panelPad}`}>
              <div className={s.dt}>Parámetros de evaluación</div>
              <div className={s.dl} style={{ marginTop: 8 }}>
                <div>
                  <div className={s.dt}>Orden de administración (por defecto)</div>
                  <div className={s.dd}>
                    <select
                      className={s.select}
                      value={order}
                      onChange={(e) => setOrder(e.target.value as AdministrationOrderDefault)}
                    >
                      {cfg.motor.administrationOrderOptions.map((o) => (
                        <option key={o} value={o}>
                          {ORDER_LABEL[o]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <div className={s.dt}>Compuerta de suficiencia (§6.4)</div>
                  <div className={`${s.dd} ${s.muted}`}>{cfg.motor.sufficiencyGate}</div>
                </div>
              </div>
              <div className={s.btnRow} style={{ marginTop: 10 }}>
                <button
                  className={`${s.btn} ${s.btnPrimary}`}
                  disabled={order === cfg.editable.administrationOrderDefault}
                  onClick={() =>
                    setPendiente({
                      titulo: "Cambiar orden de administración por defecto",
                      patch: { administrationOrderDefault: order },
                    })
                  }
                >
                  Guardar
                </button>
              </div>
            </div>

            {/* 2. Dialectos LATAM */}
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Dialectos LATAM habilitados</div>
              <div className={s.btnRow} style={{ marginTop: 8 }}>
                {dialectos.map((d) => (
                  <span key={d} className={`${s.badge} ${s.badgeBlue}`}>
                    {d}
                    <button
                      className={s.btn}
                      style={{ marginLeft: 6, padding: "0 6px" }}
                      onClick={() => setDialectos(dialectos.filter((x) => x !== d))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className={s.toolbar} style={{ marginTop: 10 }}>
                <input
                  className={s.search}
                  placeholder="Agregar dialecto (ej. es-CO)"
                  value={nuevoDialecto}
                  onChange={(e) => setNuevoDialecto(e.target.value)}
                  onKeyDown={(e) => (e.key === "Enter" ? agregarDialecto() : null)}
                />
                <button className={s.btn} onClick={agregarDialecto}>
                  Agregar
                </button>
                <button
                  className={`${s.btn} ${s.btnPrimary}`}
                  disabled={
                    dialectos.length === 0 ||
                    JSON.stringify(dialectos) === JSON.stringify(cfg.editable.dialectosHabilitados)
                  }
                  onClick={() =>
                    setPendiente({
                      titulo: "Actualizar dialectos habilitados",
                      patch: { dialectosHabilitados: dialectos },
                    })
                  }
                >
                  Guardar
                </button>
              </div>
            </div>

            {/* 3. Notificaciones */}
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Notificaciones</div>
              <div style={{ marginTop: 8 }}>
                {Object.keys(notif).map((k) => (
                  <label key={k} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0" }}>
                    <input
                      type="checkbox"
                      checked={notif[k] ?? false}
                      onChange={(e) => setNotif({ ...notif, [k]: e.target.checked })}
                    />
                    {k.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
              <div className={s.btnRow} style={{ marginTop: 10 }}>
                <button
                  className={`${s.btn} ${s.btnPrimary}`}
                  disabled={JSON.stringify(notif) === JSON.stringify(cfg.editable.notificaciones)}
                  onClick={() =>
                    setPendiente({ titulo: "Actualizar notificaciones", patch: { notificaciones: notif } })
                  }
                >
                  Guardar
                </button>
              </div>
            </div>

            {/* 4. Integraciones (solo lectura) */}
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Integraciones</div>
              <div className={s.tableWrap} style={{ marginTop: 8 }}>
                <table className={s.table}>
                  <tbody>
                    {cfg.integraciones.map((i) => (
                      <tr key={i.clave}>
                        <td>{i.nombre}</td>
                        <td>
                          {i.conectado ? (
                            <span className={`${s.badge} ${s.badgeGreen}`}>Conectado</span>
                          ) : (
                            <span className={`${s.badge} ${s.badgeNeutral}`}>No configurado</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Versiones del motor (solo lectura) */}
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Versiones del motor (solo lectura)</div>
              <div className={s.dl} style={{ marginTop: 8 }}>
                <div>
                  <div className={s.dt}>Scoring</div>
                  <div className={`${s.dd} ${s.mono}`}>{cfg.motor.scoringVersion}</div>
                </div>
                <div>
                  <div className={s.dt}>Engine (M1)</div>
                  <div className={`${s.dd} ${s.mono}`}>{cfg.motor.engineVersion}</div>
                </div>
                <div>
                  <div className={s.dt}>Inventario</div>
                  <div className={`${s.dd} ${s.mono}`}>{cfg.motor.inventoryVersion}</div>
                </div>
                <div>
                  <div className={s.dt}>Inventario PEFF</div>
                  <div className={`${s.dd} ${s.mono}`}>{cfg.motor.peffInventoryVersion}</div>
                </div>
              </div>
            </div>
          </>
        )}
      </Async>

      {pendiente ? (
        <ConfirmDialog
          titulo={pendiente.titulo}
          cuerpo={<>¿Confirmas este cambio de configuración global? Afecta producción y queda auditado.</>}
          confirmar="Confirmar cambio"
          ocupado={ocupado}
          onConfirm={() => void ejecutar()}
          onCancel={() => setPendiente(null)}
        />
      ) : null}
    </>
  );
}
